import PageHeading from "components/PageHeading.component";
import { Card, ConfigProvider, Flex, Table, TableProps, InputRef } from "antd";
import { CustomerTable } from "shared/types";
import { getAllCustomerByBusinessId } from "hooks/queries/customer";
import { useNavigate, useSearchParams } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { useEffect, useRef, useState } from "react";
import { FilterValue } from "antd/lib/table/interface";
import NewCustomerModal from "components/modals/NewCustomerModal.component";
import { PAGE_SIZE } from "shared/constants";
import { useRememberedTablePage } from "hooks/useRememberedTablePage";
import FileUpload from "components/buttons/CsvUpload.component";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import StatBand from "components/StatBand.component";
import { th, tnum } from "shared/components/TableText";
import { translate } from "shared/languages";
import { formatPhone } from "shared/utils/phone";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { ACCOUNT_STATUS_LABELS, ACCOUNT_STATUS_TONE } from "shared/utils";

// The band's segments mirror the table's Status filter values; "all" means
// no filter (both statuses visible).
type StatusSegment = "all" | "active" | "deactivated";

// Management pages show only TIMELESS facts about the list itself (counts,
// composition); anything time-windowed or ranked lives in Analitik. The old
// KPI strip here (Klien Aktif Minggu Ini, Pendapatan Bulan Ini) used calendar
// windows that disagree with Analitik's rolling windows for similar-sounding
// labels, so it was replaced by this stat strip + a deep link into Analitik.
const CustomersPage = () => {
  const navigate = useNavigate();
  // Returns active AND deactivated customers; the table's Status column
  // default-filters to active client-side.
  const { data: customers, isLoading } = getAllCustomerByBusinessId();
  const { page, handleTableChange } = useRememberedTablePage("customers");

  const totalCount = customers?.length ?? 0;
  const inactiveCount =
    customers?.filter((customer) => !!customer.deleted_at).length ?? 0;
  const activeCount = totalCount - inactiveCount;

  // Single source of truth for the Status column's filteredValue; the band
  // highlight and the table both read it. Fresh visits keep the page's
  // long-standing default: active clients only.
  const [statusFilter, setStatusFilter] = useState<string[] | null>([
    "active",
  ]);
  // A single-value filter maps to that segment; no filter (or both boxes
  // ticked in the column dropdown) reads as "all".
  const activeSegment: StatusSegment =
    statusFilter?.length === 1
      ? (statusFilter[0] as StatusSegment)
      : "all";

  const selectSegment = (key: StatusSegment) => {
    // Re-tapping the selected slice widens back to everyone (the dashboard
    // activity-strip idiom).
    const next = key === "all" || key === activeSegment ? null : [key];
    setStatusFilter(next);
    // AntD auto-resets pagination only on dropdown-driven filter changes; a
    // controlled filteredValue change must reset the page itself.
    handleTableChange({ current: 1 });
  };

  const searchInput = useRef<InputRef>(null);

  // AntD runs a table's filters as either all-controlled or all-uncontrolled.
  // The band-driven column above is controlled, which flips the whole table
  // into controlled mode, so the search columns must be controlled as well or
  // AntD silently drops whatever the user types into them.
  const [searchFilters, setSearchFilters] = useState<
    Record<string, FilterValue | null>
  >({});

  // The ?ref=<CODE> referral deep link (from the public /ref card's
  // "Saya admin" link): capture the code once, clear it from the URL, and
  // open Isi Klien Baru prefilled.
  const [searchParams, setSearchParams] = useSearchParams();
  const [referralDeepLink, setReferralDeepLink] = useState<string | null>(
    null
  );
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (!ref) return;
    setReferralDeepLink(ref);
    searchParams.delete("ref");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const columns: TableProps<CustomerTable>["columns"] = [
    {
      title: th(translate("label.name")),
      dataIndex: "full_name",
      render: (name: string) => <span style={{ fontWeight: 600 }}>{name}</span>,
      sorter: (a, b) => (a.full_name ?? "").localeCompare(b.full_name ?? ""),
      defaultSortOrder: "ascend",
      ...getColumnSearchProps<CustomerTable, "full_name">({
        config: "full_name",
        searchInput,
      }),
      filteredValue: searchFilters.full_name ?? null,
    },
    {
      title: th(translate("label.phone_number")),
      dataIndex: "phone_number",
      key: "phone_number",
      ...getColumnSearchProps<CustomerTable, "phone_number">({
        config: { dataIndex: "phone_number", searchType: "phone" },
        searchInput,
      }),
      filteredValue: searchFilters.phone_number ?? null,
      render: (phone_number: string, _: CustomerTable) => (
        <span style={tnum}>
          {formatPhone(phone_number)}
        </span>
      ),
    },
    {
      title: th(translate("label.remaining_packages")),
      dataIndex: "remaining_packages",
      key: "remaining_packages",
      render: (value: number) => <span style={tnum}>{value}</span>,
      sorter: (a, b) => (a.remaining_packages > b.remaining_packages ? 1 : -1),
    },
    {
      title: th("Status"),
      dataIndex: "deleted_at",
      key: "deleted_at",
      filters: [
        { text: ACCOUNT_STATUS_LABELS.active, value: "active" },
        { text: ACCOUNT_STATUS_LABELS.inactive, value: "deactivated" },
      ],
      // Deactivated clients are hidden by default; the stat band (or this
      // dropdown) reveals them. Filter values must be strings: AntD
      // round-trips dropdown selections as string keys, so boolean values
      // never match once the user touches it.
      filteredValue: statusFilter,
      onFilter: (value, record) => (record.deleted_at ? "deactivated" : "active") === value,
      render: (_, user) => {
        return user?.deleted_at ? (
          <StatusBadge tone={ACCOUNT_STATUS_TONE.inactive} label={ACCOUNT_STATUS_LABELS.inactive} />
        ) : (
          <StatusBadge tone={ACCOUNT_STATUS_TONE.active} label={ACCOUNT_STATUS_LABELS.active} />
        );
      },
    },
  ];

  return (
    <>
      <PageHeading title={translate("customer")} />
      <Flex vertical gap={20}>
        {/* Timeless list facts only (no time windows, no rankings): each
            segment filters the table to its slice (re-tap to widen back to
            everyone), so "Tidak Aktif" clients are one tap away instead of
            hiding behind the column-filter dropdown. */}
        <StatBand
          segments={[
            {
              key: "all",
              value: totalCount,
              label: translate("list_header.total_clients"),
            },
            // Status words follow the badges they filter (fixed English);
            // the count label stays localised.
            { key: "active", value: activeCount, label: ACCOUNT_STATUS_LABELS.active },
            { key: "deactivated", value: inactiveCount, label: ACCOUNT_STATUS_LABELS.inactive },
          ]}
          selectedKey={activeSegment}
          onSelect={(key) => selectSegment(key as StatusSegment)}
        />
        <Card styles={{ body: { padding: 18 } }}>
          {/* Toolbar: Unggah CSV (secondary) + new-customer CTA (navy). The
              modal-buttons theme also gives the modal its ink Batal. */}
          <Flex justify={"flex-end"} gap={10} style={{ marginBottom: 14 }}>
            <FileUpload endpoint={"customer"} templateUrl="/template-import-klien.csv" />
            <ConfigProvider theme={modalButtonsTheme}>
              <NewCustomerModal
                defaultOpen={!!referralDeepLink}
                initialReferralCode={referralDeepLink ?? undefined}
              />
            </ConfigProvider>
          </Flex>
          <Table
            showSorterTooltip={false}
            sticky
            rowKey="id"
            size={"middle"}
            loading={isLoading}
            columns={columns}
            dataSource={customers}
            onRow={(record) => {
              const customer_id = record.id;
              return {
                onClick: () =>
                  navigate(`${PATHS.CUSTOMER}/${customer_id}?tab=1`),
                style: { cursor: "pointer" },
              };
            }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
            }}
            onChange={(pagination, filters) => {
              handleTableChange(pagination);
              // Keep the band highlight in sync when the filter changes via
              // the column dropdown (fires on page/sort too; re-setting the
              // same value is a no-op).
              setSearchFilters(filters);
              setStatusFilter((filters.deleted_at as string[] | null) ?? null);
            }}
          ></Table>
        </Card>
      </Flex>
    </>
  );
};

export default CustomersPage;
